import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import PaletteSheet from 'LuxKit/PaletteSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { isString } from 'lib/string/stringUtils'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import clsx from 'clsx'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import OfferImageCarouselWrapper from 'components/OfferList/OfferCards/OfferImageCarouselWrapper'
import { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import { nonNullable, uniqueBy } from 'lib/array/arrayUtils'
import useHasMounted from 'hooks/useHasMounted'

export const CAROUSEL_CARD_WIDTH = 320

const CAROUSEL_PROPS: React.ComponentProps<typeof OfferImageCarouselWrapper>['imageCarouselProps'] = {
  eagerLoadFirstImage: true,
}

const CAROUSEL_CARD_IMAGE_PARAMS: ImageParams = {
  fit: 'center',
  aspectRatio: '3:2',
  dpr: 2,
  mobileWidth: '180px',
  tabletWidth: '320px',
  desktopWidth: '320px',
  largeDesktopWidth: '320px',
}

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: ${rem(200)} 1fr;
  gap: ${rem(16)};
  height: 100%;
  position: relative;
  min-height: ${rem(520)};

  &.variant-opaque {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;

  &.hover {
    > :nth-child(2) {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      transition: opacity 0.3s;
      opacity: 0;
    }

    > :nth-child(1) {
      transition: opacity 0.3s;
      opacity: 1;
    }

    ${mediaHoverable} {
      &:hover {
        > :nth-child(1) {
          opacity: 0;
        }

        > :nth-child(2) {
          opacity: 1;
        }
      }
    }
  }
`

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "left right";
  grid-template-rows: 1fr;
  padding: ${rem(8)} ${rem(12)};
  pointer-events: none;
  justify-content: space-between;

  & > .left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: left;
  }

  & > .right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: right;
  }

  a, button {
    pointer-events: auto;
  }
`

const DetailsContainer = styled(Group)`
  &.variant-opaque {
    padding: 0 ${rem(16)} ${rem(16)} ${rem(16)};
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  className?: string;
  bookmarkButton?: React.ReactElement;
  description?: string | React.ReactElement;
  descriptionLines?: number;
  image: App.Image;
  location: string | React.ReactElement;
  title: string;
  priceDetails?: React.ReactElement;
  to: string;
  onClick?: () => void;
  productType?: App.ProductType;
  productTypeKind?: 'default' | 'plain';
  rating?: App.OfferRating
  urgencyLabels?: React.ReactElement;
  partnershipLabel?: React.ReactElement;
  loyaltyPointsElement?: React.ReactElement;
  loyaltyPackageUpgradeElement?: React.ReactElement;
  imageCornerLabel?: React.ReactElement;
  images?: Array<App.Image>;
  /**
   * @default default
   */
  variant?: 'default' | 'opaque'
  /**
   * Whether or not the offer tile should load it's image without
   * waiting for the tile to be in view.
   */
  eagerLoadImage?: boolean;
}

function CarouselCardSmall({
  bookmarkButton,
  description,
  descriptionLines,
  image: baseImage,
  images,
  location,
  title,
  priceDetails,
  to,
  onClick,
  productType,
  productTypeKind,
  rating,
  urgencyLabels,
  partnershipLabel,
  className,
  loyaltyPointsElement,
  loyaltyPackageUpgradeElement,
  imageCornerLabel,
  variant = 'default',
  eagerLoadImage,
  ...rest
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  const hoverImage = images?.[1]
  const isDesktop = useScreenSizeUp('tablet')
  const isMounted = useHasMounted()
  const isHoverEnabled = isMounted && isDesktop && !!hoverImage
  const isCarouselVariant = !!useOptimizelyExperiment(OptimizelyExperiments.croHomepageTilesImageCarousel)
  const carouselImages = useMemo(() => {
    return nonNullable(uniqueBy([...(images ?? []), baseImage], img => img?.id))
  }, [images, baseImage])

  const image = baseImage ?? images?.[0]

  return <CarouselCardContainer
    {...rest}
    onClick={onClick}
    target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
    to={to}
    className={clsx(className, `variant-${variant}`)}
  >
    {isCarouselVariant && (
      <OfferImageCarouselWrapper
        imageCarouselProps={CAROUSEL_PROPS}
        images={carouselImages}
        imageParams={CAROUSEL_CARD_IMAGE_PARAMS}
        productLabel={<LabelGroup className="left">{partnershipLabel}</LabelGroup>}
        bookmarkAction={
          <Group direction="horizontal" gap={4} verticalAlign="center">
            <TripGuard>{bookmarkButton}</TripGuard>
          </Group>
        }
        onImageLoad={handleImageLoaded}
      />
    )}
    {(!isCarouselVariant) && (
      <ImageContainer
        className={clsx({ hover: isHoverEnabled })}
        paletteType="default"
      >
        <Image
          aspectRatio="3:2"
          image={image}
          fit="center"
          dpr={2}
          height="200"
          width="320"
          onLoad={handleImageLoaded}
          loading={eagerLoadImage ? 'eager' : undefined}
        />
        {hoverImage && isHoverEnabled && (
          <Image
            aspectRatio="3:2"
            image={hoverImage}
            fit="center"
            dpr={2}
            height="200"
            width="320"
          />
        )}
        <ImageOverlay>
          <LabelGroup className="left">{partnershipLabel}</LabelGroup>
          {imageCornerLabel}
          <TripGuard>
            <div className="right">
              {bookmarkButton}
            </div>
          </TripGuard>
        </ImageOverlay>
      </ImageContainer>
    )}
    <DetailsContainer
      direction="vertical"
      verticalAlign="space-between"
      gap={16}
      className={`variant-${variant}`}
    >
      <Group direction="vertical" horizontalAlign="start" gap={8}>
        {urgencyLabels}
        <Group direction="vertical" gap={4}>
          {isString(location) ? <Caption colour="neutral-one" variant="large">{location}</Caption> : location}
          <Heading lineClamp={3} variant="heading6">{title}</Heading>
        </Group>
        {productType && <ProductTypeLabel productType={productType} kind={productTypeKind ?? 'plain'} />}
        {rating && <OfferRating
          variant="xs"
          rating={rating}
          inlineLabel
          hideLink
          onlyNumber="auto"
        />}
        {description && isString(description) && <BodyText colour="neutral-two" lineClamp={descriptionLines || 2} variant="medium">{description}</BodyText>}
        {description && !isString(description) && description}
        {loyaltyPackageUpgradeElement}
        {loyaltyPointsElement}
      </Group>
      <div>
        {priceDetails}
      </div>
    </DetailsContainer>
  </CarouselCardContainer>
}

export default CarouselCardSmall
